/* An Event's loadout — STORY-029 and STORY-030.
 *
 * ── the second entry point, and the SAME action ─────────────────────────────
 * Packing for a night is done while looking at the night, so equipment is
 * taken, managed and marked returned from the Event itself as well as from
 * Inventory. Both go through the same hooks against the same records: two
 * separate "take equipment" flows would drift, and the one used less would be
 * the one that rots.
 *
 * The return follow-up is the shared `ReturnControl` for the same reason —
 * three entry points that asked differently meant the condition was captured or
 * lost depending on which screen somebody happened to be on.
 *
 * **Sharing the control was not enough, and this file is the proof.** It
 * rendered the control only while the row was still out, so the refetch that
 * follows a successful return hid it — unmounting the question that had just
 * opened. The condition could never be captured from this screen, under a
 * header comment claiming the opposite. `useReturnHolding` is the rule that
 * actually prevents it, and both screens use it now.
 *
 * ── bulk is the first-class path ────────────────────────────────────────────
 * The reference workbook's equipment sheet had zero rows: the control was
 * designed and never sustained by hand. If recording a loadout takes more than
 * a few taps while loading a van, it will go unused here too — so the picker is
 * a multi-select and one action assigns all of it.
 */

import type { Assignment } from '@eventuley/contracts';
import { formatDate } from '@eventuley/i18n';
import { useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout } from '../../../shared/ui/index.js';
import {
  useAssignEquipment,
  useInventory,
  useLoadout,
  useUnassignEquipment,
} from '../api/index.js';
import { CONDITION_LABELS, ReturnControl } from './return-control.js';
import { type ReturnHolding, useReturnHolding } from './use-return-holding.js';

export function Loadout({
  organisationId,
  eventId,
}: {
  readonly organisationId: string;
  readonly eventId: string;
}) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();
  const { data, error, isLoading } = useLoadout(organisationId, eventId);
  const [picking, setPicking] = useState(false);
  const holding = useReturnHolding();

  if (error) {
    return (
      <section className="settings__section">
        <h2>{t('inventory.loadout_heading')}</h2>
        <Callout tone="danger">
          {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      </section>
    );
  }

  const assignments = data?.assignments ?? [];
  const stillOut = assignments.filter((row) => row.returnedAt === null);

  return (
    <section className="settings__section">
      <h2>{t('inventory.loadout_heading')}</h2>

      <div className="btn-row">
        <Button variant="secondary" onClick={() => setPicking(!picking)}>
          {picking ? t('inventory.close_form') : t('inventory.loadout_add')}
        </Button>
        {/* Packing down happens at once, not item by item. */}
        {stillOut.length > 0 ? (
          <ReturnControl
            organisationId={organisationId}
            assignmentIds={stillOut.map((row) => row.id)}
            label={t('inventory.loadout_return_all')}
            /* Held, so the whole loadout's question survives the refetch that
               marks every row returned. */
            onReturned={() => {
              for (const row of stillOut) holding.hold(row);
            }}
            onDone={() => {
              for (const row of stillOut) holding.release(row.id);
            }}
          />
        ) : null}
      </div>

      {picking ? (
        <Picker
          organisationId={organisationId}
          eventId={eventId}
          alreadyOn={assignments.map((row) => row.item.id)}
          onDone={() => setPicking(false)}
        />
      ) : null}

      {isLoading && assignments.length === 0 ? (
        <p className="field__hint">{t('common.loading')}</p>
      ) : assignments.length === 0 ? (
        <p className="field__hint">{t('inventory.loadout_empty')}</p>
      ) : (
        <ul className="row-list">
          {assignments.map((assignment) => (
            <LoadoutRow
              key={assignment.id}
              organisationId={organisationId}
              assignment={assignment}
              locale={locale}
              holding={holding}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function LoadoutRow({
  organisationId,
  assignment,
  locale,
  holding,
}: {
  readonly organisationId: string;
  readonly assignment: Assignment;
  readonly locale: string;
  readonly holding: ReturnHolding;
}) {
  const t = useTranslate();
  const remove = useUnassignEquipment(organisationId);

  /* Still out, OR back and still holding its question. The second half is the
   * one that was missing: without it the refetch hides the control the instant
   * the return lands, and the question goes with it. */
  const held = holding.isHeld(assignment.id);
  const showsControl = assignment.returnedAt === null || held;

  return (
    <li className="row">
      <span className="row__main">
        <span className="row__title">{assignment.item.name}</span>
        <span className="row__meta">
          {assignment.returnedAt === null
            ? t(CONDITION_LABELS[assignment.item.condition])
            : t('inventory.loadout_returned', {
                /* An INSTANT, unlike every calendar date on the other screens —
                   so it renders in the READER's zone rather than UTC. Rendering
                   it as UTC would show the wrong day for anybody who marked
                   something back late in the evening. */
                date: formatDate(new Date(assignment.returnedAt), {
                  locale,
                  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                }),
              })}
        </span>
      </span>
      <span className="row__actions">
        {showsControl ? (
          <ReturnControl
            organisationId={organisationId}
            assignmentIds={[assignment.id]}
            label={t('inventory.mark_returned')}
            startAsking={held}
            onReturned={() => holding.hold(assignment)}
            onDone={() => holding.release(assignment.id)}
          />
        ) : null}
        {/* Takes the assignment off, never the item. Only while it is out —
            something already back is not on a loadout to remove. */}
        {assignment.returnedAt === null && !held ? (
          <Button
            variant="ghost"
            busy={remove.isPending}
            onClick={() => remove.mutate(assignment.id)}
          >
            {t('inventory.loadout_remove')}
          </Button>
        ) : null}
      </span>
    </li>
  );
}

function Picker({
  organisationId,
  eventId,
  alreadyOn,
  onDone,
}: {
  readonly organisationId: string;
  readonly eventId: string;
  readonly alreadyOn: readonly string[];
  readonly onDone: () => void;
}) {
  const t = useTranslate();
  const { data } = useInventory(organisationId);
  const assign = useAssignEquipment(organisationId, eventId);
  const [chosen, setChosen] = useState<readonly string[]>([]);

  /* Anything already on this loadout is not offered. One assignment per item
   * per Event, so offering it would be offering a refusal. */
  const available = (data?.items ?? []).filter((item) => !alreadyOn.includes(item.id));

  return (
    <div className="card">
      <fieldset className="filters__group">
        <legend className="field__label">{t('inventory.loadout_choose')}</legend>
        {available.map((item) => (
          <label className="filters__check" key={item.id}>
            <input
              type="checkbox"
              checked={chosen.includes(item.id)}
              onChange={() =>
                setChosen(
                  chosen.includes(item.id)
                    ? chosen.filter((id) => id !== item.id)
                    : [...chosen, item.id],
                )
              }
            />
            {item.name}
            {/* Its condition, in words. Taking something already marked lost is
                permitted — the team may have found it — so the reader is told
                rather than stopped. */}
            <span className="row__meta">{t(CONDITION_LABELS[item.condition])}</span>
          </label>
        ))}
      </fieldset>

      {assign.error ? (
        <Callout tone="danger">
          {assign.error instanceof NetworkError
            ? t('error.network')
            : assign.error instanceof ApiError
              ? assign.error.message
              : t('error.unexpected')}
        </Callout>
      ) : null}

      <div className="btn-row">
        <Button
          variant="primary"
          busy={assign.isPending}
          disabled={chosen.length === 0}
          onClick={() =>
            /* One action for the whole selection. */
            assign.mutate(
              { items: chosen.map((itemId) => ({ itemId })) },
              {
                onSuccess: () => {
                  setChosen([]);
                  onDone();
                },
              },
            )
          }
        >
          {t('inventory.loadout_submit')}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t('inventory.cancel')}
        </Button>
      </div>
    </div>
  );
}
